/**
 * @fileoverview "Plant something" (E25): the sheet's plan for the plant picked, its one Add (the plant at Planning and
 * its to-do "Plant <name>", together) and that Add's Undo (both taken away; after E26's "Use that one", the to-do alone
 * or its old When). The server decides every word, the day the sheet starts on and the to-do's title; the app sends
 * what was picked and draws the answer.
 *
 * Where: features/own-todos usePlantSomething.
 * API: GET /todo/own/plant/plan?cropId&plantId&varietyId&locationId&lang → PlantPlan (E26: already, the plant of that
 * kind already in the bed chosen, or null);
 * POST /todo/own/plant { cropId?, plantId?, varietyId?, locationId, when, day?, already?, lang, unit, days, device } →
 * PlantSomethingAnswer (said: the toast, its Undo, openKey); 400 TODO_BAD_REQUEST / VARIETY_NOT_YOURS / BED_NOT_YOURS,
 * 402 FREE_PLANT_LIMIT (Free's words, in message); POST /todo/own/plant/undo { key, lang, unit, days, device } →
 * PlantSomethingAnswer (said: "Deleted: …", no Undo); 404 TODO_JOB_NOT_FOUND.
 *
 * @module api/plantSomething
 */

import { apiClient } from './apiClient';
import type { TodoAsk } from './todo';

/** The plant picked in the search: a row's ids as the search sent them (GET /plants/search). */
export interface PlantPick {
  cropId: number | null;
  plantId: number | null;
  varietyId: number | null;
}

/** A choice when the bed already has a plant of that kind (E26): its key, as Add sends it back. */
export type AlreadyKey = 'use' | 'add';

/** The plant of that kind already in the bed chosen (E26), as the server says it. */
export interface PlantAlready {
  /** "You already have Romaine Lettuce in Bed 1 (planning since Oct 30)." */
  line: string;
  /** The server's default: "use" at Planning, "add" when sown, growing or with no stage. */
  choice: AlreadyKey;
  /** "Use that one" and "Add another", each with the check-mark line the sheet shows while it is chosen. */
  choices: Array<{ key: AlreadyKey; label: string; onList: string }>;
}

/** The server's plan for the plant picked. */
export interface PlantPlan {
  /** Its name as its card will title it: "Romaine Lettuce". */
  title: string;
  /** The When the sheet starts on: Today, or a day when its planting days open later. */
  when: 'today' | 'day';
  /** For 'day': Grow's best day, "YYYY-MM-DD"; null otherwise. */
  day: string | null;
  /** "Grow recommends: start indoors mid-February to mid-March"; null when it has no planting dates there. */
  recommends: string | null;
  /** "Romaine Lettuce will be on your Plants list in planning stage." */
  onList: string;
  /** The plant of that kind already in the bed chosen (E26); null when there is none. */
  already: PlantAlready | null;
}

/** What Add sends beyond the pick: the bed, and the When. */
export interface PlantSomethingNew extends PlantPick {
  locationId: number;
  when: 'today' | 'week' | 'day';
  /** For 'day': "YYYY-MM-DD", today or later. */
  day?: string;
  /** E26: "Use that one" or "Add another", sent only when the plan had a plant already there. */
  already?: AlreadyKey;
}

/** What the server says after Add or its Undo: the toast, its Undo (Add's takes back what it did), and the to-do's key. */
export interface PlantSomethingSaid {
  toast: string | null;
  /** action 'plantUndo': this module's undoPlantSomething, with the to-do's key. */
  undo: { action: string; key: string; text: string } | null;
  /** The new to-do's key in the list, for its group to show and scroll to; null for none. */
  openKey: string | null;
}

/** Add's or Undo's answer: saved, and what to say; the page reads Today again. */
export interface PlantSomethingAnswer {
  success: boolean;
  saved: boolean;
  said?: PlantSomethingSaid;
}

/**
 * The plan for the plant picked, in the bed chosen.
 *
 * @param pick - The search row's ids.
 * @param locationId - The bed chosen (its spring delay), or null while none is.
 * @param lang - The app's language.
 * @returns The server's plan.
 */
export async function getPlantPlan(pick: PlantPick, locationId: number | null, lang: string): Promise<PlantPlan> {
  const params: Record<string, string | number> = { lang };
  if (pick.cropId != null) params.cropId = pick.cropId;
  if (pick.plantId != null) params.plantId = pick.plantId;
  if (pick.varietyId != null) params.varietyId = pick.varietyId;
  if (locationId != null) params.locationId = locationId;
  const response = await apiClient.get<PlantPlan & { success: boolean }>('/todo/own/plant/plan', { params });
  const { title, when, day, recommends, onList, already } = response.data;
  return { title, when, day, recommends, onList, already };
}

/**
 * Saves the plant at Planning and its to-do, together.
 *
 * @param plant - What was picked, the bed and the When.
 * @param ask - How the brief was asked for (language, unit, Coming up's days, device).
 * @returns Saved, with said.
 */
export async function addPlantSomething(plant: PlantSomethingNew, ask: TodoAsk): Promise<PlantSomethingAnswer> {
  const response = await apiClient.post<PlantSomethingAnswer>('/todo/own/plant', { ...plant, lang: ask.lang, unit: ask.unit, days: ask.days, device: ask.device });
  return response.data;
}

/**
 * Takes an Add back, as its key says: its to-do and its plant; after "Use that one" (E26), the to-do it added alone, or
 * the old When of the to-do it moved.
 *
 * @param key - The to-do's key, as the toast's Undo carries it.
 * @param ask - How the brief was asked for.
 * @returns Saved, with said.
 */
export async function undoPlantSomething(key: string, ask: TodoAsk): Promise<PlantSomethingAnswer> {
  const response = await apiClient.post<PlantSomethingAnswer>('/todo/own/plant/undo', { key, lang: ask.lang, unit: ask.unit, days: ask.days, device: ask.device });
  return response.data;
}
